import type { AfterViewInit, OnDestroy } from '@angular/core'
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  signal,
  inject,
  input,
  output,
  viewChild
} from '@angular/core'
import { ModalComponent } from '../../layout/modal/modal.component'
import type { Observable } from 'rxjs'
import { AutoUnsubscribe } from '@awork/_shared/decorators/auto-unsubscribe'
import { hideDelay } from '../../../functions/hide-delay'

import { ButtonComponent } from '../../buttons/button/button.component'
import { Color } from '@awork/_shared/types/color'
import type { MainSize } from '@awork/_shared/types/size'
import { Size } from '@awork/_shared/types/size'
import { InfoBoxComponent } from '../../ui-help/info-box/info-box.component'
import type { ButtonColor, ButtonState } from '../../buttons/button/types'
import { InfoBoxWidth } from '../../ui-help/info-box/types'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

@AutoUnsubscribe()
@Component({
  selector: 'aw-confirmation-modal',
  templateUrl: './confirmation-modal.component.html',
  styleUrls: ['./confirmation-modal.component.scss'],
  imports: [ModalComponent, ButtonComponent, InfoBoxComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ConfirmationModalComponent implements AfterViewInit, OnDestroy {
  private cdr = inject(ChangeDetectorRef)
  private destroyRef = inject(DestroyRef)

  readonly title = input.required<string>()
  readonly subtitle = input<string>()
  readonly centerSubtitle = input(false)
  readonly highlightedText = input<string>()
  readonly image = input<{
    path: string
    alt: string
    width: number
    height: number
  }>()

  readonly actionText = input.required<string>()
  readonly cancelText = input<string>(q.translations.common.cancel)
  readonly loadingText = input<string>('')
  readonly actionColor = input<ButtonColor>(Color.Main)
  readonly size = input<MainSize>(Size.s)

  readonly loadingObservable = input<Observable<unknown>>()
  readonly delayHide = input<boolean>(false)
  readonly zIndex = input<number>()
  readonly autoShow = input(true)

  readonly confirm = output<void>()
  readonly cancel = output<void>()
  readonly hiding = output<void>()
  readonly actionEmitted = output<'confirm' | 'cancel'>()

  readonly modal = viewChild<ModalComponent>('modal')

  protected readonly buttonState = signal<ButtonState>('default')
  protected readonly Size = Size
  protected readonly InfoBoxWidth = InfoBoxWidth

  ngAfterViewInit(): void {
    if (this.autoShow()) {
      setTimeout(() => {
        this.show()
      })
    }
  }

  ngOnDestroy() {}

  /**
   * Show the delete project status modal
   */
  show(): void {
    const modal = this.modal()

    if (modal && modal.show) {
      modal.show()

      this.cdr.markForCheck()
    }
  }

  /**
   * Cancel the confirmation
   */
  onCancel(): void {
    this.cancel.emit()
    this.actionEmitted.emit('cancel')

    this.hide()
  }

  /**
   * Hide the delete project status modal
   */
  hide(): void {
    this.modal().hide()

    setTimeout(() => {
      this.hiding.emit()
      this.cdr.markForCheck()
    }, 0)

    this.buttonState.set('default')
  }

  /**
   * Emits event for confirmation
   */
  onConfirm(): void {
    this.confirm.emit()
    this.actionEmitted.emit('confirm')

    const loadingObservable = this.loadingObservable()

    if (!loadingObservable) {
      if (this.delayHide()) {
        this.buttonState.set('success')
        hideDelay(() => this.hide())
      } else {
        this.hide()
      }
    } else {
      this.buttonState.set('loading')

      loadingObservable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(loading => {
        if (loading) {
          this.buttonState.set('success')
          hideDelay(() => this.hide())
        } else {
          this.buttonState.set('default')
        }
      })
    }
  }
}
